import { computed, defineComponent, h, toRef, useSlots } from "vue";
import { Masonry } from "@eudonet/opal";
import { useEntityLayout } from "@/shared/composables/useEntityLayout";
import { evaluateFieldVisibility } from "@/shared/composables/useFieldVisibility";
import OrderedBlock from "./OrderedBlock.vue";
import GenericBlock from "./GenericBlock.vue";
import { logger } from "@/infrastructure/services/LoggerService";
/**
 * Orchestrates block rendering for an entity detail page.
 *
 * - Sorts blocks by order (ascending), delegated to useEntityLayout.
 * - kind === 'hardcoded' → <OrderedBlock>
 *   Forwards parent slots using the "{blockId}:{fieldKey}" naming convention.
 *   Parent provides: <template #main-info:legalName="{ rules }">...</template>
 *   LayoutRenderer strips the block prefix and forwards "legalName" to OrderedBlock.
 * - kind === 'generic' → <GenericBlock> (fully self-contained)
 * - extraItems: custom widgets injected at a specific order position via "#extra:{id}" slots.
 *   Consumer provides: <template #extra:addresses>...</template>
 *
 * A render function is used because Vue 3 templates cannot forward scoped slots
 * with dynamic names computed at runtime.
 */
export default defineComponent({
    name: "LayoutRenderer",
    props: {
        layout: {
            type: Object,
            required: true,
        },
        // i18n-translated headers keyed by block id, e.g. { "main-info": t("...") }
        // Falls back to empty string when not provided.
        headers: {
            type: Object,
            default: () => ({}),
        },
        // When true, wraps the rendered blocks in a <Masonry> so each block
        // becomes a direct Masonry child (required for masonry layout to work —
        // wrapping <LayoutRenderer> in an outer <Masonry> only gives Masonry one item).
        masonry: {
            type: Boolean,
            default: false,
        },
        // Custom widgets to inject between blocks at a specific order position.
        // The slot name convention is "extra:{id}", e.g. <template #extra:addresses>.
        // Use fractional order values to place between two blocks, e.g. order: 1.5.
        extraItems: {
            type: Array,
            default: () => [],
        },
        // Ids of the hardcoded blocks rendered with a row gap (see OrderedBlock rowGap),
        // e.g. ["communication"] whose read-only rows reserve no helper line.
        rowGapBlockIds: {
            type: Array,
            default: () => [],
        },
    },
    setup(props) {
        const slots = useSlots();
        const { blocks } = useEntityLayout(toRef(props, "layout"));
        const sortedItems = computed(() => {
            const blockItems = blocks.value.map((block) => ({
                kind: "block",
                order: block.order,
                block,
            }));
            const extraItems = props.extraItems.map((item) => ({
                kind: "extra",
                order: item.order,
                id: item.id,
            }));
            return [...blockItems, ...extraItems].sort((a, b) => a.order - b.order);
        });
        return () => {
            const nodes = sortedItems.value.flatMap((item) => {
                if (item.kind === "extra") {
                    const slotFn = slots[`extra:${item.id}`];
                    if (!slotFn)
                        return [];
                    // Wrap in a thin component so its own render effect tracks reactive deps
                    // (e.g. props.addresses in the parent) and re-renders independently.
                    return [h({ render: () => slotFn() })];
                }
                const { block } = item;
                if (block.kind === "hardcoded") {
                    // Skip blocks that have no parent slots yet (gradual migration support).
                    const childSlots = {};
                    for (const fieldKey of block.fieldOrder) {
                        const parentSlotName = `${block.id}:${fieldKey}`;
                        const parentSlot = slots[parentSlotName];
                        if (!parentSlot)
                            continue;
                        const rule = block.fieldRules[fieldKey];
                        if (!evaluateFieldVisibility(rule, block.values))
                            continue;
                        childSlots[fieldKey] = (slotProps) => parentSlot(slotProps);
                    }
                    if (Object.keys(childSlots).length === 0) {
                        if (import.meta.env.DEV) {
                            logger.debug(`[LayoutRenderer] Block "${block.id}" has no matching slots and will be skipped.`);
                        }
                        return [];
                    }
                    const actionsSlotName = `${block.id}:actions`;
                    const actionsSlot = slots[actionsSlotName];
                    if (actionsSlot) {
                        childSlots["actions"] = () => actionsSlot({});
                    }
                    const header = props.headers[block.id] ?? "";
                    const rowGap = props.rowGapBlockIds.includes(block.id);
                    return [h(OrderedBlock, { block, header, rowGap }, childSlots)];
                }
                if (block.kind === "generic") {
                    const blockValues = Object.fromEntries(block.fields.map((f) => [f.key, f.value]));
                    const hasVisible = block.fields.some((f) => evaluateFieldVisibility(f.rules, blockValues));
                    if (!hasVisible)
                        return [];
                    return [h(GenericBlock, { block })];
                }
                return [];
            });
            if (props.masonry) {
                return h(Masonry, { breakpoint: 1280, useViewport: true }, { default: () => nodes });
            }
            return nodes;
        };
    },
});
